import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/sq';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  toLanguageTag,
} from './languages';

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    const applyLanguage = (language: string) => {
      const normalizedLanguage = normalizeLanguage(language);
      const languageTag = toLanguageTag(normalizedLanguage);

      if (language !== normalizedLanguage && i18n.language !== normalizedLanguage) {
        i18n.changeLanguage(normalizedLanguage);
        return;
      }

      document.documentElement.lang = languageTag;
      dayjs.locale(languageTag);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
    };

    applyLanguage(i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE);
    i18n.on('languageChanged', applyLanguage);

    return () => {
      i18n.off('languageChanged', applyLanguage);
    };
  }, [i18n]);

  return <>{children}</>;
};
